import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Column, IconButton, Popover, Row, TextField } from 'seagull';
import { CloseCross } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
export const GetEditTitlePopoverTID = (parentTestId)=>getTestIds(parentTestId);
const MAX_TITLE_LENGTH = 80;
export const EditTitlePopover = (param)=>{
    let { anchorEl, initialValue, onSave, onClose, testId } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetEditTitlePopoverTID(testId), [
        testId
    ]);
    const [value, setValue] = useState(initialValue);
    const [showEmptyError, setShowEmptyError] = useState(false);
    const inputRef = useRef(null);
    const isTooLong = value.length > MAX_TITLE_LENGTH;
    const hasError = showEmptyError || isTooLong;
    const helperText = isTooLong ? t.customPages.editTitle.tooLongError() : showEmptyError ? t.customPages.editTitle.emptyError() : undefined;
    const handleSave = useCallback(()=>{
        const trimmed = value.trim();
        if (!trimmed) {
            setShowEmptyError(true);
            return;
        }
        if (trimmed.length > MAX_TITLE_LENGTH) {
            return;
        }
        onSave(trimmed);
        onClose();
    }, [
        value,
        onSave,
        onClose
    ]);
    const handleChange = useCallback((e)=>{
        const newValue = e.target.value;
        setValue(newValue);
        if (newValue.trim()) {
            setShowEmptyError(false);
        }
    }, []);
    const handleKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        }
    }, [
        handleSave,
        onClose
    ]);
    return /*#__PURE__*/ _jsx(Popover, {
        open: true,
        anchorEl: anchorEl,
        onClose: onClose,
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
        },
        children: /*#__PURE__*/ _jsxs(Column, {
            testId: testIds.EditTitlePopoverTestIds.Root(),
            padding: "16px",
            gap: 16,
            onKeyDown: handleKeyDown,
            style: {
                width: 400,
                maxWidth: '90vw'
            },
            children: [
                /*#__PURE__*/ _jsx(Row, {
                    justifyContent: "end",
                    children: /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'close_edit_custom_page_title'
                        },
                        testId: testIds.EditTitlePopoverTestIds.CloseButton(),
                        skin: "transparent",
                        size: "smallMedium",
                        onClick: onClose,
                        "aria-label": t.customPages.editTitle.close(),
                        children: /*#__PURE__*/ _jsx(CloseCross, {
                            size: 16
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(TextField, {
                    bi: {
                        name: 'custom_page_title'
                    },
                    testId: testIds.EditTitlePopoverTestIds.TitleField(),
                    label: t.customPages.editTitle.label(),
                    value: value,
                    onChange: handleChange,
                    error: hasError,
                    helperText: helperText,
                    inputRef: inputRef,
                    autoFocus: true
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 10,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'cancel_edit_custom_page_title'
                            },
                            testId: testIds.EditTitlePopoverTestIds.CancelButton(),
                            skin: "primaryOutline",
                            fluid: true,
                            onClick: onClose,
                            children: t.customPages.editTitle.cancel()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'save_custom_page_title'
                            },
                            testId: testIds.EditTitlePopoverTestIds.SaveButton(),
                            fluid: true,
                            onClick: handleSave,
                            children: t.customPages.editTitle.save()
                        })
                    ]
                })
            ]
        })
    });
};
